import { useTranslations } from 'next-intl';
import type { FC, MouseEvent, ReactNode } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { Loading } from '@/app/components/icons/Loading';
import { cn } from '@/app/utils';

interface ToolMediaUploadSurfaceProps {
  className?: string;
  isUploading: boolean;
  hasMedia: boolean;
  previewContent?: ReactNode;
  emptyContent: ReactNode;
  onEmptyClick?: () => void;
  isMobile?: boolean;
  onRemoveMedia?: () => void;
}

export const ToolMediaUploadSurface: FC<ToolMediaUploadSurfaceProps> = ({
  className,
  isUploading,
  hasMedia,
  previewContent,
  emptyContent,
  onEmptyClick,
  isMobile = false,
  onRemoveMedia,
}) => {
  const t = useTranslations();

  return (
    <div className={className}>
      {isUploading ? (
        <div className="flex flex-col items-center justify-center gap-2">
          <Loading className="animate-spin text-2xl text-system-white" />
          <p className="text-sm text-system-white/60">{t('create.createForm.upload.uploading')}</p>
        </div>
      ) : hasMedia && previewContent ? (
        <div className="group relative size-full rounded-[inherit]">
          <div className="size-full overflow-hidden rounded-[inherit]">{previewContent}</div>
          {onRemoveMedia ? (
            <button
              type="button"
              className={cn(
                '-translate-y-1/2 absolute top-0 right-0 z-10 flex size-5 translate-x-1/2 items-center justify-center rounded-full bg-white/40 text-system-white transition-all hover:bg-white/70',
                isMobile
                  ? 'opacity-100'
                  : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100',
              )}
              onClick={onRemoveMedia}
            >
              <BaseLineClose className="size-3" />
            </button>
          ) : null}
        </div>
      ) : (
        <div
          className={cn(
            'size-full',
            !isUploading && !hasMedia && 'flex flex-col items-center justify-center',
            onEmptyClick && 'cursor-pointer',
          )}
          onClick={(event: MouseEvent<HTMLDivElement>) => {
            if (!onEmptyClick || (event.target as Element).closest('button')) return;
            onEmptyClick();
          }}
        >
          {emptyContent}
        </div>
      )}
    </div>
  );
};
